import AnimatorConflicts from "../../../../components/Meetings/AnimatorConflicts";
import ApiService from "../../../../services/ApiService";
import { calculateEndDate, getLocalHourToUTC } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { Animator, FormData, MeetingTypes } from "./CohortMeetings.d";

export const getSuggestedNames = (
  meetingType: MeetingTypes | null,
  selectedProgram: string | null,
  selectedLanguage: string
): string[] => {
  if (!meetingType?.suggested_names) return [];
  if (selectedLanguage && selectedProgram)
    return meetingType.suggested_names?.[selectedLanguage]?.[selectedProgram] || [];
  else if (selectedLanguage) {
    if (!meetingType.suggested_names?.[selectedLanguage]) return [];
    return [...Object.values(meetingType.suggested_names?.[selectedLanguage]).flatMap(String)];
  } else
    return [
      ...Object.keys(meetingType.suggested_names)
        .map((language) => Object.values(meetingType.suggested_names[language]).flatMap(String))
        .flat(),
    ];
};

export const fetchMeetingTypes = (setMeetingTypes: Function, language: string) => {
  ApiService.get("meetings/types?get_suggested_names=true")
    .then((response) => setMeetingTypes(response.data.meeting_types))
    .catch((err) => {
      console.error("Error fetching meeting types:", err);
      alert(translate(language, "PAGES.MEETINGS.ERROR.FETCH_MEETING_TYPES"));
    });
};

export const getMeetings = (cohortId: string, setMeetings: Function, language: string, setLoading: Function) => {
  setLoading(true);
  ApiService.get(`cohorts/${cohortId}/meetings`)
    .then((response) => {
      setMeetings(response.data.meetings);
    })
    .catch((error) => {
      console.error("Failed to get meetings:", error);
      setMeetings([]);
      alert(translate(language, "PAGES.COHORT.MEETINGS.ERRORS.GET_MEETINGS"));
    })
    .finally(() => setLoading(false));
};

export const getAdmins = (setAnimators: Function, setPrograms: Function, language: string) => {
  ApiService.get("meetings/resources?only_active_animators=true")
    .then((response) => {
      setAnimators(response.data.meeting_staff_and_programs.animators);
      setPrograms(response.data.meeting_staff_and_programs.programs);
    })
    .catch((error) => {
      console.error("Failed to get animators and programs:", error);
      alert(translate(language, "PAGES.COHORT.MEETINGS.ERRORS.GET_ADMINS"));
    });
};

export const createMeeting = (
  cohortId: string,
  formData: FormData,
  language: string,
  setIsMeetingCreating: Function,
  displayAlert: Function,
  setShowModal: Function,
  resetSelection: Function
) => {
  const meeting_type = formData.selectedMeetingType;
  const startDate = new Date(formData.startDate).toISOString();

  const payload = {
    type_id: meeting_type?.id,
    dt_start: startDate,
    dt_end:
      meeting_type?.name.toLowerCase() === "multiple days"
        ? new Date(formData.endDate).toISOString()
        : calculateEndDate(formData.startDate, formData.duration),
    name: formData.title,
    visibility: formData.selectedVisibility,
    language: formData.selectedLanguage,
    program_id: formData.selectedProgram,
    animator_ids: formData.selectedAnimators.map((animator: Animator) => animator.staff_member_id),
    service_link_generation: formData.meetingLinkService,
    description: formData.description,
    url: formData.url,
    days:
      meeting_type?.name.toLowerCase() === "multiple days" && formData.selectedDays.length > 0
        ? formData.selectedDays.map((day: any) => day.id)
        : undefined,
    morning_end_date: formData.morningEnd ? getLocalHourToUTC(formData.morningEnd, startDate.split("T")[0]) : "",
    morning_start_date: formData.morningStart ? getLocalHourToUTC(formData.morningStart, startDate.split("T")[0]) : "",
    afternoon_end_date: formData.afternoonEnd ? getLocalHourToUTC(formData.afternoonEnd, startDate.split("T")[0]) : "",
    afternoon_start_date: formData.afternoonStart
      ? getLocalHourToUTC(formData.afternoonStart, startDate.split("T")[0])
      : "",
    project_id:
      meeting_type?.name.toLowerCase() === "call projet" && formData.selectedProject
        ? formData.selectedProject
        : undefined,
  };

  setIsMeetingCreating(true);

  ApiService.post(`cohorts/${cohortId}/meetings`, payload)
    .then((response) => {
      const data = response.data;

      const meetingIds = data.meeting_id ? [data.meeting_id] : data.meeting_ids || [];

      if (data.animator_conflicts) {
        displayAlert({
          type: "warning",
          title: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.WARNING.TITLE"),
          message: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.WARNING.MESSAGE"),
          extraContent: <AnimatorConflicts conflicts={data.animator_conflicts} />,
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.WARNING.OPEN_CONFLICTS"),
              variant: "secondary",
              onClick: () =>
                data.animator_conflicts
                  .flatMap((conflict: any) => conflict.conflicting_meetings.map((meeting: any) => meeting.id))
                  .forEach((id: string) => window.open(`/meeting/${id}`, "_blank")),
            },
          ],
        });
      } else {
        displayAlert({
          type: "success",
          title: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.SUCCESS.TITLE"),
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.SUCCESS.OPEN_MEETINGS"),
              variant: "secondary",
              onClick: () => meetingIds.forEach((id: string) => window.open(`/meeting/${id}`, "_blank")),
            },
            {
              label: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.SUCCESS.CREATE_MEETING"),
              variant: "primary",
              onClick: () => setShowModal(true),
            },
          ],
        });
      }
      resetSelection();
    })
    .catch((error) => {
      console.error("Failed to create meeting:", error);

      displayAlert({
        type: "danger",
        title: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.ERROR.TITLE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.COHORT.MEETINGS.DST_ALERT.ERROR.TRY_AGAIN"),
            variant: "primary",
            onClick: () => setShowModal(true),
          },
        ],
      });
    })
    .finally(() => setIsMeetingCreating(false));
};

export const getProjects = (cohortId: Number, setProjects: Function, language: string) => {
  ApiService.get(`cohorts/${cohortId}/projects`)
    .then((response) => {
      setProjects(
        response.data.projects.map((project: { id: number; name: string }) => ({
          value: project.id,
          display: project.name,
        }))
      );
    })
    .catch((err) => {
      console.error("Error fetching projects:", err);
      alert(translate(language, "PAGES.COHORT.MEETINGS.ERRORS.FETCH_PROJECTS"));
    });
};

export const styleMeetingsRow = (meetings: any): string => {
  return new Date(meetings.dt_end).getTime() < Date.now() ? "table-secondary" : "";
};
